import { resolveMediaUrl } from '../../api/client';
import { highlightRanges } from '../../api/catalog';
import { formatDuration } from '../../lib/format';
import { Icon } from '../../ui';
import { Highlighted } from '../library/Highlighted';
import { guestSongRowAriaLabel } from './guestSongRowLabel';
import { useT } from '../../i18n/i18n.tsx';
import type { Song } from '../../api/types';

/**
 * DESIGN.md §9.3 [Requests] `GuestSongRow` — a >=44px, one-handed tap target (AC-REQ-8), no
 * horizontal scroll at 360px. Its own row rather than library-ui's `SongRow`: DESIGN.md's "two
 * surfaces, two languages" (hard rule #3) — a phone never needs selection state, density modes or
 * a hover thumbstrip, so this stays a small, independent component instead of widening that one.
 * Reuses `highlightRanges`/`Highlighted` (library-ui's already-tested pure helpers) for the same
 * bolded-match treatment AC-UI-3 gives the operator's results.
 */
export function GuestSongRow({ song, query, onPick }: { song: Song; query: string; onPick: (song: Song) => void }) {
  const t = useT();
  const artist = song.artistNameCache?.trim();
  const titleRanges = highlightRanges(song.title, query);
  const artistRanges = artist ? highlightRanges(artist, query) : [];

  // P2 fix (round 5): the button's only content was two highlighted-span fragments plus a bare
  // duration string — no accessible name at all (confirmed live: the accessibility tree showed a
  // bare `button` with no name). `aria-label` states the actual action, not just the title, since
  // screen-reader users hear this instead of the visible text entirely. Logic lives in
  // `guestSongRowLabel.ts` (pure, unit-tested — named distinctly from this file so a bare,
  // extension-less `./GuestSongRow` import elsewhere never resolves to it on a case-insensitive
  // filesystem; see that file's own doc comment). MVP-272 Phase 1: that module returns a key+params
  // pair rather than the rendered sentence itself, resolved here via `t()`.
  // MVP-369: clauses, joined with a space. The join lives here rather than in the pure module for
  // the same reason the sentence does not: only this file has `t()`. A space and nothing else —
  // neither dictionary punctuates between these two clauses, and Thai does not use a comma here.
  const accessibleLabel = guestSongRowAriaLabel(song.title, artist, song.catalogId)
    .map((clause) => t(clause.key, clause.params))
    .join(' ');

  return (
    <button type="button" className="guest-song-row" onClick={() => onPick(song)} aria-label={accessibleLabel} data-testid="guest-song-row">
      {song.poster?.url ? (
        <img className="guest-song-row__thumb" src={resolveMediaUrl(song.poster.url)} alt="" loading="lazy" />
      ) : (
        <span className="guest-song-row__thumb guest-song-row__thumb--placeholder" aria-hidden="true">
          <Icon name="play" size={18} />
        </span>
      )}
      <span className="guest-song-row__meta">
        <span className="guest-song-row__title text-body truncate">
          <Highlighted text={song.title} ranges={titleRanges} />
        </span>
        <span className="guest-song-row__artist text-meta truncate">
          {artist ? <Highlighted text={artist} ranges={artistRanges} /> : t('guest.songRow.unknownArtist')}
        </span>
      </span>
      {/* MVP-274 R1: the catalogue code has been on the wire since `toSongCard` (`search.js`) and
          printed next to every song in the venue's book, but this row never showed it. That made the
          book and the phone two unrelated catalogues: a customer who found their song here could not
          tell the KJ its number, and a customer who found it in the book could not confirm the row
          they were about to tap was the same song. Leads the row's end, ahead of the duration, for
          the same reason `SongRow` puts it first — it is the handle, not a detail. */}
      {/* MVP-364: the `title={t('guest.songRow.catalogueCode')}` that used to sit here was dead
          weight twice over. This surface is a phone, and a tooltip never fires on touch; and the
          span lives inside a button whose `aria-label` (`guestSongRowAriaLabel`) replaces all of
          its children, so assistive tech never reached it either. The `#` prefix and the venue's
          printed book are what carry the meaning for the reader who can see it.
          MVP-369 closed the other half: the code is now a clause of that `aria-label`, so the
          reader who cannot see this span hears the number instead of missing it. */}
      {song.catalogId && <span className="guest-song-row__code text-meta tabular-nums">#{song.catalogId}</span>}
      <span className="guest-song-row__end text-meta tabular-nums">{formatDuration(song.durationMs)}</span>
    </button>
  );
}
